import { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { Minus, Plus, Check, ArrowRight, Loader2, Users as UsersIcon } from "lucide-react";
import { toast } from "sonner";

import Nav from "@/components/Nav";
import FloatingActions from "@/components/FloatingActions";
import { useI18n, MEDIA, BRAND } from "@/i18n";
import {
  BYE_EXPERIENCES,
  BYE_MENUS,
  BYE_WINES,
  BYE_WINE_CATEGORIES,
  BYE_DEPOSIT_EUR,
} from "@/lib/buildEveningData";
import { trackEvent } from "@/lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EXPERIENCE_IMAGES = {
  coppie: MEDIA.terrace,
  gruppo: MEDIA.hero,
};

export default function BuildYourEvening() {
  const { t, lang } = useI18n();
  const bye = t.classes.buildEvening;

  const [experience, setExperience] = useState("coppie");
  const [menu, setMenu] = useState("tradizione");
  const [guests, setGuests] = useState(2);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("19:30");
  const [wines, setWines] = useState({}); // { wine_id: qty }
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const selectedMenu = BYE_MENUS.find((m) => m.key === menu);

  const winesTotal = useMemo(() => {
    return Object.entries(wines).reduce((sum, [id, qty]) => {
      const w = BYE_WINES.find((x) => x.id === id);
      return w ? sum + w.price * qty : sum;
    }, 0);
  }, [wines]);

  const totalBottles = useMemo(() => Object.values(wines).reduce((a, b) => a + b, 0), [wines]);

  const experienceTotal = (selectedMenu?.price || 0) * guests;
  const grandTotal = experienceTotal + winesTotal;
  const balance = Math.max(0, grandTotal - BYE_DEPOSIT_EUR);

  const addWine = (id) => setWines((w) => ({ ...w, [id]: (w[id] || 0) + 1 }));
  const removeWine = (id) =>
    setWines((w) => {
      const next = { ...w };
      if ((next[id] || 0) <= 1) delete next[id];
      else next[id] = next[id] - 1;
      return next;
    });
  const setGuestsSafe = (n) => {
    const min = experience === "coppie" ? 2 : 4;
    const max = experience === "coppie" ? 2 : 8;
    setGuests(Math.max(min, Math.min(max, n)));
  };

  // When experience changes, snap guests to its min
  useEffect(() => {
    if (experience === "coppie") setGuests(2);
    else if (experience === "gruppo" && guests < 4) setGuests(4);
    // eslint-disable-next-line
  }, [experience]);

  const canSubmit = experience && menu && date && name && email && phone;

  const buildMessage = () => {
    const expLabel = bye.experiences[experience]?.title || experience;
    const menuLabel = bye.menus[menu]?.name || menu;
    const lines = [
      `--- Build Your Evening ---`,
      `Experience: ${expLabel}`,
      `Menu: ${menuLabel} (€${selectedMenu.price}/p)`,
      `Guests: ${guests}`,
      `Experience total: €${experienceTotal}`,
    ];
    if (totalBottles > 0) {
      lines.push(`Wines (${totalBottles}):`);
      Object.entries(wines).forEach(([id, qty]) => {
        const w = BYE_WINES.find((x) => x.id === id);
        if (w) lines.push(`  - ${qty}× ${w.name} = €${w.price * qty}`);
      });
      lines.push(`Wines total: €${winesTotal}`);
    }
    lines.push(`Grand total: €${grandTotal}`);
    lines.push(`Deposit now: €${BYE_DEPOSIT_EUR}`);
    lines.push(`Balance on the night: €${balance}`);
    lines.push(`---`);
    if (notes) lines.push(`Guest notes: ${notes}`);
    return lines.join("\n");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      toast.error(bye.selectRequired);
      return;
    }
    setSubmitting(true);
    try {
      trackEvent("begin_checkout", {
        value: BYE_DEPOSIT_EUR,
        currency: "EUR",
        class_type: experience,
        guests: Number(guests),
        language: lang,
        bye_menu: menu,
        bye_total: grandTotal,
      });
      const payload = {
        name,
        email,
        phone,
        class_type: experience,
        date,
        time_slot: time,
        guests: Number(guests),
        message: buildMessage(),
        language: lang,
        origin_url: window.location.origin,
      };
      const res = await axios.post(`${API}/bookings/checkout`, payload);
      window.location.href = res.data.checkout_url;
    } catch (err) {
      if (process.env.NODE_ENV === "development") console.error(err);
      const msg = err?.response?.data?.detail || bye.bookingError;
      toast.error(msg);
      setSubmitting(false);
    }
  };

  // Set SEO
  useEffect(() => {
    const originalTitle = document.title;
    document.title = `${bye.heroTitle} · Eternal City View`;
    return () => { document.title = originalTitle; };
  }, [bye.heroTitle]);

  return (
    <div className="bg-[#FAF9F6] min-h-screen">
      <Nav />

      {/* Hero */}
      <section className="relative min-h-[55vh] flex items-end overflow-hidden" data-testid="bye-hero">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${MEDIA.terrace})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#14110D]/95 via-[#14110D]/55 to-[#14110D]/20" />
        <div className="absolute inset-0 grain" />
        <div className="relative max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-10 pb-14 sm:pb-20 pt-32 text-[#FAF9F6]">
          <p className="overline text-[#D4B98D]">{bye.heroOverline}</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-3 max-w-3xl">
            {bye.heroTitle}
          </h1>
          <p className="mt-4 text-lg text-[#FAF9F6]/85 max-w-2xl">{bye.heroSub}</p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-20">
        <form onSubmit={onSubmit} className="grid lg:grid-cols-12 gap-10 lg:gap-14">
          {/* LEFT — selectors */}
          <div className="lg:col-span-7 space-y-14">
            {/* STEP 1 — Experience */}
            <div data-testid="bye-step-experience">
              <StepHeader n={1} title={bye.step1Title} />
              <div className="grid sm:grid-cols-2 gap-4 mt-6">
                {BYE_EXPERIENCES.map((key) => {
                  const e = bye.experiences[key];
                  const active = experience === key;
                  return (
                    <button
                      type="button"
                      key={key}
                      onClick={() => setExperience(key)}
                      data-testid={`bye-experience-${key}`}
                      className={`text-left rounded-sm overflow-hidden transition-all ${
                        active
                          ? "ring-2 ring-[#C85A3C] shadow-lg"
                          : "ring-1 ring-[#2C2520]/15 hover:ring-[#2C2520]/40"
                      }`}
                    >
                      <div className="aspect-[4/3] relative">
                        <img src={EXPERIENCE_IMAGES[key]} alt={e.title} className="absolute inset-0 w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                        {active && (
                          <span className="absolute top-3 right-3 bg-[#C85A3C] text-white rounded-full p-1.5">
                            <Check className="w-4 h-4" strokeWidth={2.5} />
                          </span>
                        )}
                        <div className="absolute bottom-0 left-0 right-0 p-4 text-[#FAF9F6]">
                          <p className="font-serif-display text-xl leading-tight">{e.title}</p>
                          <p className="text-xs text-[#FAF9F6]/80 mt-1">{e.persons}</p>
                        </div>
                      </div>
                      <div className="p-4 bg-white">
                        <p className="text-sm text-[#2C2520]/75 leading-relaxed">{e.desc}</p>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Guests */}
              <div className="mt-6 flex items-center gap-4">
                <span className="overline">{bye.guestsLabel}</span>
                <GuestStepper
                  value={guests}
                  min={experience === "coppie" ? 2 : 4}
                  max={experience === "coppie" ? 2 : 8}
                  onChange={setGuestsSafe}
                />
              </div>
            </div>

            {/* STEP 2 — Menu */}
            <div data-testid="bye-step-menu">
              <StepHeader n={2} title={bye.step2Title} />
              <div className="grid gap-3 mt-6">
                {BYE_MENUS.map((m) => {
                  const text = bye.menus[m.key] || { name: m.key, desc: "" };
                  const active = menu === m.key;
                  return (
                    <button
                      type="button"
                      key={m.key}
                      onClick={() => setMenu(m.key)}
                      data-testid={`bye-menu-${m.key}`}
                      className={`relative p-5 sm:p-6 rounded-sm bg-white text-left transition ${
                        active
                          ? "ring-2 ring-[#C85A3C] shadow-md"
                          : "ring-1 ring-[#2C2520]/10 hover:ring-[#2C2520]/30"
                      }`}
                    >
                      <div className="flex items-start gap-3 flex-wrap">
                        <p className="font-serif-display text-xl sm:text-2xl text-[#1F1A14]">{text.name}</p>
                        {m.badge && (
                          <span className="text-[9px] uppercase tracking-widest bg-[#1F1A14] text-[#D4B98D] px-2 py-0.5 rounded-sm mt-1.5">
                            Premium
                          </span>
                        )}
                        <span className="ml-auto text-sm whitespace-nowrap">
                          <span className="font-medium text-[#C85A3C]">€{m.price}</span>
                          <span className="text-[#2C2520]/55"> {bye.perPerson}</span>
                        </span>
                      </div>
                      <p className="text-sm text-[#2C2520]/70 mt-2 leading-relaxed">{text.desc}</p>
                      {active && (
                        <span className="absolute top-4 right-4 bg-[#C85A3C] text-white rounded-full p-1">
                          <Check className="w-3.5 h-3.5" strokeWidth={2.5} />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* STEP 3 — Wines */}
            <div data-testid="bye-step-wines">
              <StepHeader n={3} title={bye.step3Title} />
              <p className="text-sm text-[#2C2520]/60 mt-2 italic">{bye.wineNote}</p>

              <div className="mt-6 space-y-8">
                {BYE_WINE_CATEGORIES.map((cat) => {
                  const list = BYE_WINES.filter((w) => w.cat === cat);
                  return (
                    <div key={cat}>
                      <p className="overline text-[#B89668]">{bye.wineCategories[cat]}</p>
                      <ul className="mt-3 divide-y divide-[#2C2520]/10 bg-white rounded-sm ring-1 ring-[#2C2520]/10">
                        {list.map((w) => (
                          <li key={w.id} className="flex items-center gap-3 px-4 py-3" data-testid={`bye-wine-row-${w.id}`}>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-[#1F1A14] truncate">{w.name}</p>
                              <p className="text-xs text-[#2C2520]/55">{w.region} · {w.note}</p>
                            </div>
                            <p className="text-sm text-[#2C2520]/75 w-14 text-right">€{w.price}</p>
                            <WineStepper
                              value={wines[w.id] || 0}
                              onInc={() => addWine(w.id)}
                              onDec={() => removeWine(w.id)}
                              testid={w.id}
                            />
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* STEP 4 — Guest info */}
            <div data-testid="bye-step-info">
              <StepHeader n={4} title={bye.step4Title} />
              <div className="mt-6 grid sm:grid-cols-2 gap-5">
                <Field label={bye.nameLabel} value={name} onChange={setName} required testid="bye-name" />
                <Field label={bye.emailLabel} value={email} onChange={setEmail} type="email" required testid="bye-email" />
                <Field label={bye.phoneLabel} value={phone} onChange={setPhone} required testid="bye-phone" />
                <Field label={bye.dateLabel} value={date} onChange={setDate} type="date" required testid="bye-date" min={new Date().toISOString().slice(0,10)} />
                <Field label={bye.timeLabel} value={time} onChange={setTime} type="time" testid="bye-time" />
              </div>
              <div className="mt-5">
                <label className="block overline mb-2">{bye.notesLabel}</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  data-testid="bye-notes"
                  className="w-full bg-white border border-[#2C2520]/15 focus:border-[#C85A3C] outline-none rounded-sm px-3 py-2 text-sm text-[#1F1A14]"
                />
              </div>
            </div>
          </div>

          {/* RIGHT — sticky summary */}
          <aside className="lg:col-span-5">
            <div className="sticky top-24 bg-[#1F1A14] text-[#FAF9F6] rounded-sm p-7 shadow-lg" data-testid="bye-summary">
              <p className="overline text-[#D4B98D]">{bye.summaryTitle}</p>

              <div className="mt-5 space-y-3 text-sm">
                <SummaryRow label={bye.experienceRow} value={bye.experiences[experience]?.title} />
                <SummaryRow label={bye.menuRow} value={`${bye.menus[menu]?.name} · €${selectedMenu.price}${bye.perPerson}`} />
                <SummaryRow
                  label={bye.guestsLabel}
                  value={<span className="inline-flex items-center gap-1"><UsersIcon className="w-3.5 h-3.5" strokeWidth={1.8}/> {guests} {bye.guestsSuffix}</span>}
                />
                <SummaryRow label={`${bye.menuRow} × ${guests}`} value={`€${experienceTotal}`} />

                {totalBottles > 0 ? (
                  <div className="pt-3 mt-3 border-t border-[#FAF9F6]/10">
                    <p className="overline text-[#D4B98D] mb-2">
                      {bye.winesRow} · {totalBottles} {totalBottles === 1 ? bye.bottleLabel : bye.bottlesLabel}
                    </p>
                    <ul className="space-y-1 text-xs text-[#FAF9F6]/80 max-h-40 overflow-auto pr-1">
                      {Object.entries(wines).map(([id, qty]) => {
                        const w = BYE_WINES.find((x) => x.id === id);
                        if (!w) return null;
                        return (
                          <li key={id} className="flex justify-between gap-2">
                            <span className="truncate">{qty}× {w.name}</span>
                            <span className="shrink-0">€{w.price * qty}</span>
                          </li>
                        );
                      })}
                    </ul>
                    <p className="mt-2 text-sm flex justify-between">
                      <span className="text-[#FAF9F6]/70">{bye.winesRow}</span>
                      <span>€{winesTotal}</span>
                    </p>
                  </div>
                ) : (
                  <p className="text-xs text-[#FAF9F6]/50 italic pt-2">{bye.noWines}</p>
                )}
              </div>

              <div className="mt-6 pt-5 border-t border-[#FAF9F6]/10">
                <div className="flex justify-between items-baseline">
                  <span className="font-serif-display text-xl">{bye.totalLabel}</span>
                  <span className="font-serif-display text-3xl text-[#D4B98D]">€{grandTotal}</span>
                </div>
                <div className="mt-3 flex justify-between text-sm">
                  <span className="text-[#FAF9F6]/60">{bye.depositLabel}</span>
                  <span>€{BYE_DEPOSIT_EUR}</span>
                </div>
                <div className="mt-1 flex justify-between text-sm">
                  <span className="text-[#FAF9F6]/60">{bye.balanceLabel}</span>
                  <span>€{balance}</span>
                </div>
              </div>

              <button
                type="submit"
                disabled={!canSubmit || submitting}
                data-testid="bye-submit"
                className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-4 bg-[#C85A3C] text-white text-sm tracking-widest uppercase hover:bg-[#B24A2E] disabled:opacity-40 disabled:cursor-not-allowed transition rounded-sm"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> …</>
                ) : (
                  <>{bye.bookNow} <ArrowRight className="w-4 h-4" strokeWidth={1.8} /></>
                )}
              </button>

              <p className="mt-3 text-[11px] text-[#FAF9F6]/40 text-center leading-relaxed">
                {bye.wineNote}
              </p>
            </div>
          </aside>
        </form>
      </section>

      <FloatingActions />
    </div>
  );
}

// ---------- Little helpers ----------

function StepHeader({ n, title }) {
  return (
    <div className="flex items-center gap-4">
      <span className="w-9 h-9 rounded-full bg-[#1F1A14] text-[#D4B98D] font-serif-display flex items-center justify-center text-sm">
        {n}
      </span>
      <h2 className="font-serif-display text-2xl sm:text-3xl text-[#1F1A14]">{title}</h2>
    </div>
  );
}

function GuestStepper({ value, min, max, onChange }) {
  return (
    <div className="inline-flex items-center border border-[#2C2520]/15 rounded-sm bg-white" data-testid="bye-guests-stepper">
      <button
        type="button"
        onClick={() => onChange(value - 1)}
        disabled={value <= min}
        className="w-9 h-9 flex items-center justify-center text-[#2C2520]/70 hover:text-[#C85A3C] disabled:opacity-30"
        aria-label="minus"
        data-testid="bye-guests-minus"
      >
        <Minus className="w-4 h-4" strokeWidth={2} />
      </button>
      <span className="w-10 text-center font-medium text-[#1F1A14]" data-testid="bye-guests-value">{value}</span>
      <button
        type="button"
        onClick={() => onChange(value + 1)}
        disabled={value >= max}
        className="w-9 h-9 flex items-center justify-center text-[#2C2520]/70 hover:text-[#C85A3C] disabled:opacity-30"
        aria-label="plus"
        data-testid="bye-guests-plus"
      >
        <Plus className="w-4 h-4" strokeWidth={2} />
      </button>
    </div>
  );
}

function WineStepper({ value, onInc, onDec, testid }) {
  return (
    <div className="inline-flex items-center">
      <button
        type="button"
        onClick={onDec}
        disabled={value === 0}
        data-testid={`bye-wine-minus-${testid}`}
        className="w-8 h-8 flex items-center justify-center text-[#2C2520]/60 hover:text-[#C85A3C] disabled:opacity-30"
        aria-label="minus"
      >
        <Minus className="w-3.5 h-3.5" strokeWidth={2} />
      </button>
      <span
        data-testid={`bye-wine-qty-${testid}`}
        className={`w-6 text-center text-sm font-medium ${value > 0 ? "text-[#C85A3C]" : "text-[#2C2520]/50"}`}
      >
        {value}
      </span>
      <button
        type="button"
        onClick={onInc}
        data-testid={`bye-wine-plus-${testid}`}
        className="w-8 h-8 flex items-center justify-center text-[#2C2520]/60 hover:text-[#C85A3C]"
        aria-label="plus"
      >
        <Plus className="w-3.5 h-3.5" strokeWidth={2} />
      </button>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid, min }) {
  return (
    <div>
      <label className="block overline mb-2">{label}{required && <span className="text-[#C85A3C]"> *</span>}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        min={min}
        data-testid={testid}
        className="w-full bg-white border border-[#2C2520]/15 focus:border-[#C85A3C] outline-none rounded-sm px-3 py-2.5 text-sm text-[#1F1A14] transition-colors"
      />
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-[#FAF9F6]/60">{label}</span>
      <span className="text-right">{value}</span>
    </div>
  );
}
